/**
 * Scelta del ticker di quotazione quando il mapping automatico non basta
 * (F-040, estratto dalla `WatchlistPage` per F-071).
 *
 * Vive qui e non dentro la pagina perché lo stesso gesto serve ovunque un
 * titolo possa entrare in una lista: dalla watchlist, dalla wishlist e dalla
 * vista di dettaglio. Duplicarne una seconda copia avrebbe voluto dire due
 * elenchi di candidati destinati a divergere — e il suggerimento
 * sull'inserimento manuale, che è la parte davvero difficile da scrivere bene,
 * ripetuto due volte.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError } from '@/lib/api-client';
import { Alert, Button, EmptyState, Field, Modal, Spinner, TableWrap } from '@/ui';
import { useMappingCandidates, useSetMapping } from './mapping-api';
import { formatExchangeCode } from './openfigi-exchange-codes';
import './watchlist.css';

/**
 * Inserimento manuale del ticker: i codici borsa dei candidati OpenFIGI sono
 * codici Bloomberg ("IM", "GR", "QX"…), non riconoscibili per chi non li usa
 * di mestiere — segnalato dall'utente su un caso reale (Banco BPM: voleva
 * "BAMI.MI", nessuna riga della tabella lo diceva chiaramente). Qui l'utente
 * digita il ticker che conosce (es. da Yahoo/Google Finance, col suffisso
 * ".MI"/".L"/...): un ticker sbagliato degrada in "nessuna quotazione", mai
 * un problema, quindi non serve convalidarlo prima di provare.
 */
function ManualTickerEntry({
  setMapping,
  onResolved,
}: {
  setMapping: ReturnType<typeof useSetMapping>;
  onResolved: () => void;
}) {
  const [ticker, setTicker] = useState('');
  const isManualPending = setMapping.isPending && setMapping.variables === ticker.trim().toUpperCase();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = ticker.trim();
    if (!trimmed) return;
    setMapping.mutate(trimmed, { onSuccess: onResolved });
  };

  return (
    <form className="watchlist__manual-ticker" onSubmit={handleSubmit}>
      <Field
        label="Oppure inserisci il ticker manualmente"
        hint='Come appare su Yahoo/Google Finance, col suffisso di borsa se serve (es. "BAMI.MI" per Milano, "AZN.L" per Londra)'
        value={ticker}
        onChange={(event) => setTicker(event.target.value)}
        placeholder="BAMI.MI"
      />
      <Button type="submit" variant="secondary" loading={isManualPending} disabled={!ticker.trim()}>
        Usa questo ticker
      </Button>
    </form>
  );
}

/** Dialog con i candidati OpenFIGI già persistiti al tentativo automatico: nessuna nuova chiamata OpenFIGI. */
export function MappingCandidatesModal({
  instrumentId,
  instrumentName,
  onClose,
  onResolved,
}: {
  instrumentId: string;
  instrumentName: string;
  onClose: () => void;
  /**
   * Chiamata solo quando un ticker è stato davvero associato — mai alla
   * chiusura. Serve a chi apre il dialog *per poter proseguire* (aggiungere il
   * titolo a una lista): distinguere "risolto" da "annullato" è la differenza
   * fra proseguire e non fare nulla.
   */
  onResolved?: () => void;
}) {
  const candidatesQuery = useMappingCandidates(instrumentId, true);
  const setMapping = useSetMapping(instrumentId);
  const mutationError = setMapping.error instanceof ApiError ? setMapping.error : null;
  const handleResolved = () => {
    onResolved?.();
    onClose();
  };

  return (
    <Modal title={`Scegli il ticker per ${instrumentName}`} onClose={onClose}>
      {mutationError && (
        <Alert variant="error" title="Selezione non riuscita">
          {mutationError.message}
        </Alert>
      )}

      <ManualTickerEntry setMapping={setMapping} onResolved={handleResolved} />

      {candidatesQuery.isPending ? (
        <Spinner label="Caricamento dei candidati…" />
      ) : candidatesQuery.isError ? (
        <Alert variant="error" title="Impossibile caricare i candidati">
          {candidatesQuery.error instanceof ApiError ? candidatesQuery.error.message : 'Errore imprevisto.'}
        </Alert>
      ) : candidatesQuery.data.items.length === 0 ? (
        <EmptyState
          icon="🔍"
          title="Nessun candidato automatico disponibile"
          description="Il mapping è stato ritentato nel frattempo: chiudi e riapri per un elenco aggiornato, oppure usa l'inserimento manuale qui sopra."
        />
      ) : (
        <>
          <p className="watchlist__mapping-candidates-hint">
            "Borsa" mostra il nome della piazza quando riconosciuto; per un codice non ancora mappato resta il
            codice grezzo del fornitore dati (Bloomberg) — se non lo riconosci, usa l&apos;inserimento manuale
            qui sopra invece di indovinare.
          </p>
          <TableWrap>
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Borsa</th>
                  <th>Valuta</th>
                  <th>Tipo</th>
                  <th aria-label="Azioni" />
                </tr>
              </thead>
              <tbody>
                {candidatesQuery.data.items.map((candidate, index) => (
                  <tr key={`${candidate.ticker}-${candidate.exchange ?? ''}-${index}`}>
                    <td>{candidate.ticker}</td>
                    <td>{formatExchangeCode(candidate.exchange)}</td>
                    <td>{candidate.currency ?? '—'}</td>
                    <td>{candidate.securityType ?? '—'}</td>
                    <td>
                      <Button
                        variant="ghost"
                        loading={setMapping.isPending && setMapping.variables === candidate.ticker}
                        onClick={() => setMapping.mutate(candidate.ticker, { onSuccess: handleResolved })}
                      >
                        Scegli
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </>
      )}
    </Modal>
  );
}
